/**
 * Cross-window query invalidation, over a same-origin BroadcastChannel.
 *
 * Every window of the app keeps its own React Query cache. When a Sage card
 * completes, `components/sage/cards/refresh-on-complete.ts` invalidates the
 * lists its write made stale in the window that drew the card — and, through
 * `broadcastStaleQueryRoots`, names the same query roots to every other open
 * window of the app (a dashboard tab beside the installed /sage window, a
 * second tab). Each window's `listenForStaleQueryRoots`, mounted where the
 * QueryClient lives (components/query-provider.tsx), invalidates them there.
 *
 * Only query-key NAMES cross the channel (`['sequences']`), never record
 * data: a key's segments are short identifiers, checked on both ends. A
 * window never receives its own message, because the sender and the listener
 * share this module's one channel object. Without BroadcastChannel (an old
 * browser, a non-browser runtime) both halves do nothing.
 */

import type { QueryClient } from '@tanstack/react-query'

const CHANNEL_NAME = 'maiteams:query-roots'
const MESSAGE_TYPE = 'stale-query-roots'
const MAX_ROOTS = 16
const MAX_SEGMENTS = 3
const SEGMENT = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/

let channel: BroadcastChannel | null | undefined

/** This window's one channel object, or null where there is none to open. */
function sharedChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel
  try {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null
  } catch {
    channel = null
  }
  return channel
}

/** The well-formed query roots in `value`: arrays of short identifier strings. */
export function staleRootsFrom(value: unknown): string[][] {
  if (!Array.isArray(value)) return []
  const roots: string[][] = []
  const seen = new Set<string>()
  for (const root of value) {
    if (roots.length >= MAX_ROOTS) break
    if (!Array.isArray(root) || root.length === 0 || root.length > MAX_SEGMENTS) continue
    if (!root.every((segment) => typeof segment === 'string' && SEGMENT.test(segment))) continue
    const key = root.join('\u0000')
    if (seen.has(key)) continue
    seen.add(key)
    roots.push([...root])
  }
  return roots
}

// Cards that complete in the same render (a thread loading its history) send
// ONE message naming each root once.
let pending: string[][] = []

/** Tell every other open window of the app that these query roots are stale. */
export function broadcastStaleQueryRoots(roots: ReadonlyArray<readonly string[]>): void {
  const clean = staleRootsFrom(roots)
  if (clean.length === 0 || !sharedChannel()) return
  const queued = pending.length > 0
  pending = [...pending, ...clean]
  if (queued) return
  queueMicrotask(() => {
    const message = { type: MESSAGE_TYPE, roots: staleRootsFrom(pending) }
    pending = []
    try {
      sharedChannel()?.postMessage(message)
    } catch {
      // A closed or failing channel costs the other windows a refresh, nothing more.
    }
  })
}

/** Invalidate, in `queryClient`, every root another window names. Returns the unsubscribe. */
export function listenForStaleQueryRoots(queryClient: QueryClient): () => void {
  const ch = sharedChannel()
  if (!ch) return () => undefined
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; roots?: unknown } | null
    if (!data || data.type !== MESSAGE_TYPE) return
    for (const root of staleRootsFrom(data.roots)) void queryClient.invalidateQueries({ queryKey: root })
  }
  ch.addEventListener('message', onMessage)
  return () => ch.removeEventListener('message', onMessage)
}
